import React, { memo, useCallback } from 'react'
import styled from 'styled-components'
import { SearchOutlined, Close } from '@material-ui/icons'
import { Spacings, Text, Devices } from '@styles'

const SearchInputWrapper = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	max-width: ${({ maxWidth }) => maxWidth || '20rem'};
`

const StyledInput = styled.input`
	width: 100%;
	padding: ${Spacings.SPACING_6} ${Spacings.SPACING_8B} ${Spacings.SPACING_6} ${Spacings.SPACING_9B};
	font-size: ${Text.SMALL};
	border: 1px solid ${({ theme }) => theme.colors.border.default};
	border-radius: ${({ theme }) => theme.radius.sm};
	color: ${({ theme }) => theme.colors.text.primary};
	background: ${({ theme }) => theme.colors.surface.card};
	font-weight: ${Text.BOLD_500};
	font-family: inherit;
	transition: border-color 0.2s ease, box-shadow 0.2s ease;

	@media ${Devices.tablet} {
		padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_8B} ${Spacings.SPACING_2B} ${Spacings.SPACING_9B};
		font-size: ${Text.MEDIUM};
	}

	&:focus {
		border-color: ${({ theme }) => theme.colors.border.focus};
		outline: none;
		box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.brand.subtle};
	}

	&::placeholder {
		color: ${({ theme }) => theme.colors.text.secondary};
		font-weight: ${Text.REGULAR};
		opacity: 1;
	}
`

const SearchIconWrapper = styled.div`
	position: absolute;
	left: ${Spacings.SPACING_3B};
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${({ theme }) => theme.colors.text.secondary};
	pointer-events: none;

	& > svg {
		font-size: ${Text.LARGE};
	}

	@media ${Devices.tablet} {
		& > svg {
			font-size: ${Text.M_LARGE};
		}
	}
`

const ClearButton = styled.button`
	position: absolute;
	right: ${Spacings.SPACING_3B};
	display: flex;
	align-items: center;
	justify-content: center;
	background: none;
	border: none;
	padding: 0;
	color: ${({ theme }) => theme.colors.text.secondary};
	cursor: pointer;
	transition: color 0.15s ease;

	&:hover {
		color: ${({ theme }) => theme.colors.text.primary};
	}

	& > svg {
		font-size: ${Text.LARGE};
	}

	@media ${Devices.tablet} {
		& > svg {
			font-size: ${Text.M_LARGE};
		}
	}
`

const SearchInput = memo(({ value, onChange, placeholder = 'Search...', onClear, maxWidth, ...props }) => {
	const handleClear = useCallback(() => {
		if (onClear) {
			onClear()
		} else if (onChange) {
			onChange({ target: { value: '' } })
		}
	}, [onClear, onChange])

	return (
		<SearchInputWrapper maxWidth={maxWidth}>
			<SearchIconWrapper>
				<SearchOutlined />
			</SearchIconWrapper>
			<StyledInput
				type="text"
				value={value || ''}
				onChange={onChange}
				placeholder={placeholder}
				{...props}
			/>
			{value && (
				<ClearButton type="button" onClick={handleClear} aria-label="Clear search">
					<Close />
				</ClearButton>
			)}
		</SearchInputWrapper>
	)
})

export default SearchInput
